
import Vue, { PropType } from 'vue';

import Table from '@/components/common/Table.vue';
import AssignDeviceModal from '@/components/modal/AssignDeviceModal.vue';
import UnassignDeviceModal from '@/components/modal/UnassignDeviceModal.vue';
import DeviceEditModal from '@/components/modal/DeviceEditModal.vue';
import { LuminaireRow } from '@/interfaces/luminaires';
import { Controller } from '@/interfaces/controllers';
import { Device, OverrideInfoLuminaire, Unassign } from '@/interfaces/locations';
import { DeviceType, FilterTypes } from '@/interfaces/types';
import { Alert } from '@/interfaces/alerts';
import { BvTableFieldArray } from 'bootstrap-vue';

interface ComponentData {
	selectedController: Controller | null;
	selectedLuminaire: Device | null;
	unassignableDevice: LuminaireRow | null;
	assignableLuminaire: boolean;
}

interface LuminaireTableRow {
	id: number;
	name: string;
	type: string;
	description: string;
	alert: string[];
	power: number | string;
	identify: boolean;
	off: boolean;
	unassign: Unassign;
}

export default Vue.extend({
	components: {
		Table,
		AssignDeviceModal,
		UnassignDeviceModal,
		DeviceEditModal,
	},

	props: {
		controllers: { type: Array as PropType<Controller[]>, default: (): Controller[] => [] },
		luminaires: {
			type: Array as PropType<OverrideInfoLuminaire[]>,
			default: (): OverrideInfoLuminaire[] => [],
		},
		deviceTypes: { type: Array as PropType<DeviceType[]>, default: (): DeviceType[] => [] },
		activeAlerts: { type: Array as PropType<Alert[]>, default: (): Alert[] => [] },
		unassignedLuminaires: {
			type: Array as PropType<OverrideInfoLuminaire[]>,
			default: (): OverrideInfoLuminaire[] => [],
		},
		unassignedSwitches: {
			type: Array as PropType<OverrideInfoLuminaire[]>,
			default: (): OverrideInfoLuminaire[] => [],
		},
		userPermission: { type: Boolean, default: true },
		zoneName: { type: String },
		switches: {
			type: Array as PropType<OverrideInfoLuminaire[]>,
			default: (): OverrideInfoLuminaire[] => [],
		},
		assignableEmergencySwitches: {
			type: Array as PropType<OverrideInfoLuminaire[]>,
			default: (): OverrideInfoLuminaire[] => [],
		},
		luminairesWithOffset: {
			type: Array as PropType<OverrideInfoLuminaire[]>,
			default: (): OverrideInfoLuminaire[] => [],
		},
		roomSensors: {
			type: Array as PropType<string[]>,
			default: (): string[] => [],
		},
	},

	data(): ComponentData {
		return {
			selectedController: null,
			selectedLuminaire: null,
			unassignableDevice: null,
			assignableLuminaire: false,
		};
	},

	computed: {
		fields(): BvTableFieldArray {
			return [
				{ key: 'name', label: this.$t('Name').toString(), sortable: true },
				{ key: 'type', label: this.$t('Type').toString(), sortable: true },
				{ key: 'description', label: this.$t('Description').toString(), sortable: false },
				{ key: 'alert', label: this.$t('Alerts').toString() },
				{ key: 'power', label: this.$t('Power').toString(), sortable: true },
				{ key: 'badgeOverride', label: this.$t('Override').toString() },
				{ key: 'delete', label: '' },
			];
		},

		filter(): FilterTypes[] {
			let filteredDeviceTypes: DeviceType[] = [];
			let correctFeatureIds: number[] = [];
			for (const deviceType of this.deviceTypes) {
				let correctType = deviceType.features.length !== 1;
				if (deviceType.features.length === 1) {
					correctType = deviceType.features[0] !== 'dt_light_control';
				}
				if (correctType) {
					filteredDeviceTypes.push(deviceType);
					correctFeatureIds.push(deviceType.id);
				}
			}
			return [{ filteredDeviceTypes, correctFeatureIds }];
		},

		sensorRows(): LuminaireTableRow[] {
			let sensors: OverrideInfoLuminaire[] = [];
			for (const luminaire of this.luminaires) {
				const sensor: number | null = luminaire.fk_device_type;
				if ((sensor && this.filter[0].correctFeatureIds.includes(sensor)) || sensor === null) {
					sensors.push(luminaire);
				}
			}

			for (const _switch of this.switches) {
				const sensor: number | null = _switch.fk_device_type;
				if (sensor && this.filter[0].correctFeatureIds.includes(sensor)) {
					sensors.push(_switch);
				}
			}
			return sensors.map((sensor: OverrideInfoLuminaire) => {
				const deviceType = this.filter[0].filteredDeviceTypes.find(
					(deviceType: DeviceType) => deviceType.id === sensor.fk_device_type
				);
				const controller = this.controllers.find((c) => c.id === sensor.fk_controller) ?? undefined;
				return {
					id: sensor.id,
					name:
						controller && controller.guid
							? `${sensor.name} - ${controller.guid.slice(-4)}`
							: sensor.name,
					type: deviceType?.name ?? this.$t('Unknown').toString(),
					description: sensor.description,
					power: deviceType?.rated_power ?? '',
					alert: this.activeAlerts
						.filter((activeAlert: Alert) => {
							return activeAlert.fk_device === sensor.id;
						})
						.map((activeAlert: Alert) => {
							let answer = String(this.$t(activeAlert.fk_alert_type));
							if (activeAlert.description.length > 0) {
								answer = `${answer} : ${activeAlert.description}`;
								if (
									['1', '2', '3', '256', '257', '258', '259', '512'].includes(
										activeAlert.description
									)
								) {
									answer = `${answer} - ${this.$t(
										String(`light_control_error_${activeAlert.description}`)
									).toString()}`;
								}
							}
							return answer;
						}),
					identify: sensor.identifyActive,
					off: sensor.offActive,
					unassign: this.allowUnassign(sensor.id) ? 'on' : 'disabled',
				};
			});
		},

		filterUnassignedLuminaires(): LuminaireTableRow[] {
			let luminaires: OverrideInfoLuminaire[] = [];

			for (const unassignedLuminaire of this.unassignedLuminaires) {
				const sensor: number | null = unassignedLuminaire.fk_device_type;
				if (sensor && this.filter[0].correctFeatureIds.includes(sensor)) {
					luminaires.push(unassignedLuminaire);
				}
			}
			return luminaires.map((sensor: OverrideInfoLuminaire) => {
				const deviceType = this.filter[0].filteredDeviceTypes.find(
					(deviceType: DeviceType) => deviceType.id === sensor.fk_device_type
				);
				const guid = this.controllers
					.find((controller) => controller.id === sensor.fk_controller)
					?.guid.slice(-4);
				return {
					id: sensor.id,
					name: guid ? `${sensor.name} - ${guid}` : sensor.name,
					type: deviceType?.name ?? this.$t('Unknown').toString(),
					description: sensor.description,
					power: deviceType?.rated_power ?? '',
					alert: this.activeAlerts
						.filter((activeAlert: Alert) => {
							return activeAlert.fk_device === sensor.id;
						})
						.map((activeAlert: Alert) => String(this.$t(activeAlert.fk_alert_type))),
					identify: sensor.identifyActive,
					off: sensor.offActive,
					unassign: 'on',
				};
			});
		},

		filterUnassignedSwitches(): LuminaireTableRow[] {
			return this.unassignedSwitches.map((_switch: OverrideInfoLuminaire) => {
				const deviceType = this.filter[0].filteredDeviceTypes.find(
					(deviceType: DeviceType) => deviceType.id === _switch.fk_device_type
				);
				const guid = this.controllers
					.find((controller) => controller.id === _switch.fk_controller)
					?.guid.slice(-4);
				return {
					id: _switch.id,
					name: guid ? `${_switch.name} - ${guid}` : _switch.name,
					type: deviceType?.name ?? this.$t('Unknown').toString(),
					description: _switch.description,
					power: deviceType?.rated_power ?? '',
					alert: this.activeAlerts
						.filter((activeAlert: Alert) => {
							return activeAlert.fk_device === _switch.id;
						})
						.map((activeAlert: Alert) => String(this.$t(activeAlert.fk_alert_type))),
					identify: _switch.identifyActive,
					off: _switch.offActive,
					unassign: 'on',
				};
			});
		},
	},

	methods: {
		allowUnassign(luminaireId: number): boolean {
			let sensors = [...this.luminaires, ...this.switches];
			let selectedLuminaire: Device = sensors.find((l) => l.id === luminaireId)!;
			if (selectedLuminaire!.fk_controller !== null) {
				const selcont: Controller | null =
					this.controllers.find((c) => c.id === selectedLuminaire!.fk_controller) ?? null;
				if (selcont) {
					return !this.roomSensors?.includes(selcont.guid);
				}
			}
			return true;
		},

		assignDevice(luminaireId: number): void {
			this.$emit('assignDevice', luminaireId);
		},

		unassignDevice(luminaireId: number): void {
			this.$emit('unassignDevice', luminaireId);
		},

		async assignDeviceModal(): Promise<void> {
			this.assignableLuminaire = true;
			await this.$nextTick();
			this.$bvModal.show('modal-assign-device');
		},

		async unassignDeviceModal(luminaireRow: LuminaireRow): Promise<void> {
			if (this.allowUnassign(luminaireRow.id)) {
				this.unassignableDevice = luminaireRow;
			}
			await this.$nextTick();
			this.$bvModal.show('modal-unassign-device');
		},

		async onSensorRowClick(row: LuminaireRow): Promise<void> {
			let sensors = [...this.luminaires, ...this.switches];
			this.selectedLuminaire = sensors.find((l) => l.id === row.id)!;
			if (this.selectedLuminaire!.fk_controller !== null) {
				this.selectedController =
					this.controllers.find((c) => c.id === this.selectedLuminaire!.fk_controller) ?? null;
			}
			await this.$nextTick();
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(this.$refs.deviceEditModal as any).show();
			this.$bvModal.show('modal-unassign-device');
		},

		cleanModalData(): void {
			this.assignableLuminaire = false;
			this.unassignableDevice = null;
			this.selectedLuminaire = null;
			this.selectedController = null;
		},

		onDeviceModify(luminaire: Partial<Device>) {
			this.$emit('modifyDevice', luminaire, this.selectedLuminaire);
		},
	},
});
